/**
 * 開始畫面狀態
 * 顯示遊戲開始畫面，包含背景、角色動畫和閃電效果
 */
export default class StartScreen {

  /**
   * 創建開始畫面
   * 初始化所有視覺元素、動畫和互動
   */
  create() {
    // 播放背景音樂
    // this.game.sound.play('On_the_Bach', 1, true);

    // 設置辦公室背景
    this.setupOfficeBackground();

    // 設置標誌和點擊事件
    this.setupSign();

    // 設置閃電效果
    this.setupLightning();

    // 設置物理系統（用於粒子效果）
    this.game.physics.startSystem(Phaser.Physics.ARCADE);

    // 設置角色動畫
    this.setupPlayer();
  }

  /**
   * 設置辦公室背景
   * 創建淡入動畫效果
   */
  setupOfficeBackground() {
    const officeBg = this.game.add.sprite(
      this.game.world.centerX,
      this.game.world.centerY,
      'office_02'
    );
    officeBg.alpha = 0;
    officeBg.anchor.setTo(0.5);
    officeBg.scale.scaleMode = Phaser.ScaleManager.SHOW_ALL;
    officeBg.scale.pageAlignHorizontally = true;
    officeBg.scale.pageAlignVertically = true;
    officeBg.scale.forceLandscape = true;

    // 淡入動畫（10秒，延遲3秒）
    const fadeInTween = this.game.add.tween(officeBg).to(
      { alpha: 1 },
      10000,
      Phaser.Easing.Linear.None,
      true,
      3000
    );

    // 淡入完成後顯示標誌
    fadeInTween.onComplete.add(() => {
      if (this.sign) {
        this.game.add.tween(this.sign).to(
          { alpha: 1 },
          2000,
          Phaser.Easing.Linear.None,
          true,
          0
        );
      }
    }, this);

    this.officeBg = officeBg;
  }

  /**
   * 設置標誌和點擊事件
   * 點擊標誌後開始淡出並轉換到遊戲狀態
   */
  setupSign() {
    this.sign = this.game.add.sprite(
      this.game.world.centerX + 200,
      this.game.world.centerY + 200,
      'sign'
    );
    this.sign.anchor.setTo(0.5);
    this.sign.angle = 17;
    this.sign.alpha = 0;
    this.sign.inputEnabled = true;

    // 點擊標誌開始遊戲
    this.sign.events.onInputDown.add(() => {
      this.startGameTransition();
    }, this);
  }

  /**
   * 開始遊戲轉場動畫
   * 淡出所有元素並轉換到遊戲狀態
   */
  startGameTransition() {
    const fadeOutDuration = 6000;
    const playerFadeOutDuration = 5000;
    const stateTransitionDelay = 6000;

    // 淡出標誌
    this.game.add.tween(this.sign).to(
      { alpha: 0 },
      fadeOutDuration,
      Phaser.Easing.Linear.None,
      true,
      0
    );

    // 淡出背景
    this.game.add.tween(this.officeBg).to(
      { alpha: 0 },
      fadeOutDuration,
      Phaser.Easing.Linear.None,
      true,
      0
    );

    // 淡出角色
    this.game.add.tween(this.player).to(
      { alpha: 0 },
      playerFadeOutDuration,
      Phaser.Easing.Linear.None,
      true,
      0
    );

    // 延遲後轉換到遊戲狀態
    this.game.time.events.add(stateTransitionDelay, () => {
      this.game.state.start('game');
    }, this);
  }

  /**
   * 設置閃電效果
   * 創建閃電紋理和點擊觸發
   */
  setupLightning() {
    // 創建閃電位圖數據
    this.lightningBitmap = this.game.add.bitmapData(1280, 768);
    // 創建閃電精靈
    this.lightning = this.game.add.image(this.game.width / 2, 0, this.lightningBitmap);
    // 設置錨點在頂部中心，方便定位
    this.lightning.anchor.setTo(0.5, 0);

    // 點擊或觸摸時觸發閃電
    this.game.input.onTap.add(this.zap, this);

    // 設置獎章粒子發射器（如果資源存在）
    try {
      this.medal_emitter = this.game.add.emitter(
        this.game.world.centerX + 80,
        this.game.world.centerY - 20,
        500
      );
      this.medal_emitter.makeParticles('medal', Phaser.ArrayUtils.numberArray(0, 631));
      this.medal_emitter.gravity = 200;
    } catch (e) {
      // 如果medal資源不存在，忽略錯誤
      console.warn('Medal particle resource not available');
    }
  }

  /**
   * 設置角色動畫
   * 創建角色精靈並設置所有動畫
   */
  setupPlayer() {
    this.player = this.game.add.sprite(128, this.game.world.height - 150, 'dude');
    this.player.anchor.setTo(0.5, 0.5);

    // 添加所有動畫
    this.player.animations.add('face', Phaser.ArrayUtils.numberArray(0, 11), 12, true);
    this.player.animations.add('left', Phaser.ArrayUtils.numberArray(12, 23), 12, true);
    this.player.animations.add('right', Phaser.ArrayUtils.numberArray(24, 35), 12, true);
    this.player.animations.add('back', Phaser.ArrayUtils.numberArray(36, 47), 12, true);

    // 播放背面動畫
    this.player.animations.play('back');

    // 角色放大動畫（15秒）
    this.game.add.tween(this.player.scale).to(
      { x: 3, y: 3 },
      15000,
      Phaser.Easing.Linear.None,
      true
    );
  }

  /**
   * 更新循環
   * 目前無需每幀更新
   */
  update() {
    // 可以在此添加每幀更新的邏輯
  }

  /**
   * 創建閃電效果
   * 在隨機位置生成閃電並播放閃爍動畫
   */
  zap() {
    // 在隨機X位置創建閃電紋理（20個線段，寬度3）
    this.createLightningTexture(this.game.world.randomX, 0, 20, 3, false);

    // 顯示閃電
    this.lightning.alpha = 1;

    // 播放閃爍動畫：快速閃爍幾次後淡出
    this.game.add.tween(this.lightning)
      .to({ alpha: 0.5 }, 100, Phaser.Easing.Bounce.Out)
      .to({ alpha: 1.0 }, 100, Phaser.Easing.Bounce.Out)
      .to({ alpha: 0.5 }, 100, Phaser.Easing.Bounce.Out)
      .to({ alpha: 1.0 }, 100, Phaser.Easing.Bounce.Out)
      .to({ alpha: 0 }, 250, Phaser.Easing.Cubic.In)
      .start();
  }

  /**
   * 創建閃電紋理
   * 使用Canvas繪製閃電效果
   * @param {number} x - 起始X座標
   * @param {number} y - 起始Y座標
   * @param {number} segments - 線段數量
   * @param {number} boltWidth - 閃電寬度
   * @param {boolean} branch - 是否為分支
   */
  createLightningTexture(x, y, segments, boltWidth, branch) {
    const ctx = this.lightningBitmap.context;
    const width = this.lightningBitmap.width;
    const height = this.lightningBitmap.height;

    // 清除畫布（僅主閃電）
    if (!branch) {
      ctx.clearRect(0, 0, width, height);
    }

    // 繪製每個線段
    for (let i = 0; i < segments; i++) {
      ctx.strokeStyle = 'rgb(255, 255, 255)';
      ctx.lineWidth = boltWidth;
      ctx.beginPath();
      ctx.moveTo(x, y);

      // 計算X偏移（分支和主閃電使用不同的範圍）
      const xOffset = branch
        ? this.game.rnd.integerInRange(-10, 10)
        : this.game.rnd.integerInRange(-30, 30);
      x += xOffset;

      // 限制X座標在畫布範圍內
      x = Math.max(10, Math.min(width - 10, x));

      // 計算Y偏移
      const yOffset = branch
        ? this.game.rnd.integerInRange(10, 20)
        : this.game.rnd.integerInRange(20, height / segments);
      y += yOffset;

      // 確保主閃電到達底部
      if ((!branch && i === segments - 1) || y > height) {
        y = height;
      }

      // 繪製線段
      ctx.lineTo(x, y);
      ctx.stroke();

      // 到達底部時停止
      if (y >= height) break;

      // 20%機率在主閃電上創建分支
      if (!branch && Phaser.Utils.chanceRoll(20)) {
        this.createLightningTexture(x, y, 10, 1, true);
      }
    }

    // 標記紋理需要更新
    this.lightningBitmap.dirty = true;
  }
}
